import type {
  AttendanceExportRow,
  GroupExportFields,
  GroupMembershipsByUserId,
  UserExportRow,
} from '../types/export.types';
import type { EventRsvpWithProfile } from '../types/rsvps.types';
import type { EventAttendanceWithProfile } from '../types/attendance.types';
import type {
  Event,
  GroupMembershipBadgeRow,
  UserProfile,
} from '../types/database';
import type { Guest } from '../types/database';
import { formatMemberSinceDate } from './users.utils';
import { getRoleColorLabelByKey, getRoleLabelByKey } from './roles.utils';

/**
 * Escape a CSV field value to handle special characters
 * Wraps value in quotes if it contains commas, quotes, or newlines
 */
function escapeCSVField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    // Escape quotes by doubling them, then wrap in quotes
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Convert event type to display format (title case)
 */
function formatEventType(eventType: string | null): string {
  if (!eventType || eventType.trim() === '') {
    return 'Unknown';
  }
  // Convert to title case: "master class" -> "Master Class"
  return eventType
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function sortGroupMemberships(
  groups: GroupMembershipBadgeRow[]
): GroupMembershipBadgeRow[] {
  return [...groups].sort((a, b) => {
    const nameComparison = a.display_name.localeCompare(b.display_name);
    if (nameComparison !== 0) {
      return nameComparison;
    }

    return a.import_key.localeCompare(b.import_key);
  });
}

export function buildGroupMembershipsByUserId(
  memberships: GroupMembershipBadgeRow[]
): GroupMembershipsByUserId {
  const byUserId = new Map<string, GroupMembershipBadgeRow[]>();

  memberships.forEach((membership) => {
    const groups = byUserId.get(membership.user_id) ?? [];
    groups.push(membership);
    byUserId.set(membership.user_id, groups);
  });

  return byUserId;
}

export function formatGroupExportFields(
  groups: GroupMembershipBadgeRow[] | undefined
): GroupExportFields {
  const sortedGroups = sortGroupMemberships(groups ?? []);

  return {
    groupImportKeys: sortedGroups.map((group) => group.import_key).join(', '),
    groupDisplayNames: sortedGroups
      .map((group) => group.display_name)
      .join(', '),
  };
}

/**
 * Get RSVP status display text
 */
function getRsvpStatusText(rsvp: EventRsvpWithProfile | undefined): string {
  if (!rsvp) {
    return 'No RSVP';
  }
  switch (rsvp.status) {
    case 'yes':
      return 'Going';
    case 'no':
      return 'Not Going';
    case 'attended':
      return 'Going';
    default:
      return 'No RSVP';
  }
}

/**
 * Check if a user attended the event
 */
function didAttend(
  userId: string,
  attendance: EventAttendanceWithProfile[]
): boolean {
  return attendance.some((record) => record.user_id === userId);
}

/**
 * Generate CSV content from attendance data
 */
export function generateAttendanceCSV(
  event: Event,
  rsvps: EventRsvpWithProfile[],
  attendance: EventAttendanceWithProfile[],
  membershipsByUserId: GroupMembershipsByUserId = new Map()
): string {
  // Create a map of all users (from RSVPs and attendance)
  const userMap = new Map<string, AttendanceExportRow>();

  // Process RSVPs
  rsvps.forEach((rsvp) => {
    const name = rsvp.profile?.full_name || rsvp.profile?.email || 'Member';
    const email = rsvp.profile?.email || null;
    const rsvpStatus = getRsvpStatusText(rsvp);
    const attended = didAttend(rsvp.user_id, attendance) ? 'Yes' : 'No';
    const eventType = formatEventType(event.event_type);
    const groupFields = formatGroupExportFields(
      membershipsByUserId.get(rsvp.user_id)
    );

    userMap.set(rsvp.user_id, {
      name,
      email,
      rsvpStatus,
      attended,
      eventType,
      ...groupFields,
    });
  });

  // Process attendance records (may include users without RSVPs)
  attendance.forEach((record) => {
    if (!userMap.has(record.user_id)) {
      const name =
        record.profile?.full_name || record.profile?.email || 'Member';
      const email = record.profile?.email || null;
      const rsvpStatus = 'No RSVP';
      const attended = 'Yes';
      const eventType = formatEventType(event.event_type);
      const groupFields = formatGroupExportFields(
        membershipsByUserId.get(record.user_id)
      );

      userMap.set(record.user_id, {
        name,
        email,
        rsvpStatus,
        attended,
        eventType,
        ...groupFields,
      });
    } else {
      // Update existing entry to mark as attended
      const existing = userMap.get(record.user_id)!;
      existing.attended = 'Yes';
    }
  });

  // Convert map to array and sort by name
  const rows = Array.from(userMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  // Generate CSV content
  const headers = [
    'Name',
    'Email',
    'RSVP Status',
    'Attended',
    'Event Type',
    'Group Import Keys',
    'Group Display Names',
  ];
  const csvRows = [
    headers.map(escapeCSVField).join(','),
    ...rows.map((row) =>
      [
        escapeCSVField(row.name),
        escapeCSVField(row.email || ''),
        escapeCSVField(row.rsvpStatus),
        escapeCSVField(row.attended),
        escapeCSVField(row.eventType),
        escapeCSVField(row.groupImportKeys),
        escapeCSVField(row.groupDisplayNames),
      ].join(',')
    ),
  ];

  return csvRows.join('\n');
}

function getUserExportName(profile: UserProfile): string {
  return profile.full_name || profile.email || 'Member';
}

/**
 * Generate CSV content from user profile data
 */
export function generateUsersCSV(rows: UserExportRow[]): string {
  const sortedRows = [...rows].sort((a, b) =>
    getUserExportName(a.profile).localeCompare(getUserExportName(b.profile))
  );

  const headers = [
    'Name',
    'Email',
    'Role',
    'Role Color',
    'Can See All Events',
    'Member Since',
    'Group Import Keys',
    'Group Display Names',
  ];
  const csvRows = [
    headers.map(escapeCSVField).join(','),
    ...sortedRows.map(({ profile, groups }) => {
      const groupFields = formatGroupExportFields(groups);

      return [
        escapeCSVField(getUserExportName(profile)),
        escapeCSVField(profile.email || ''),
        escapeCSVField(getRoleLabelByKey(profile.role)),
        escapeCSVField(getRoleColorLabelByKey(profile.role)),
        escapeCSVField(profile.can_see_all_events ? 'Yes' : 'No'),
        escapeCSVField(formatMemberSinceDate(profile.created_at)),
        escapeCSVField(groupFields.groupImportKeys),
        escapeCSVField(groupFields.groupDisplayNames),
      ].join(',');
    }),
  ];

  return csvRows.join('\n');
}

/**
 * Format visit date for CSV export
 */
function formatVisitDate(visitDate: string): string {
  const date = new Date(visitDate);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format date and time for CSV export
 */
function formatDateTime(dateTime: string): string {
  const date = new Date(dateTime);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Generate CSV content from guest log data
 */
export function generateGuestLogCSV(guests: Guest[]): string {
  // Convert guests to export rows
  const rows = guests.map((guest) => {
    const guestName = `${guest.first_name} ${guest.last_name}`.trim();
    const registeredBy = guest.registered_by_name || 'Unknown';

    return {
      guestName,
      email: guest.email,
      visitDate: formatVisitDate(guest.visit_date),
      reasonForVisit: guest.reason_for_visit,
      registeredBy,
    };
  });

  // Generate CSV content
  const headers = [
    'Guest Name',
    'Email',
    'Visit Date',
    'Reason for Visit',
    'Registered By',
  ];
  const csvRows = [
    headers.map(escapeCSVField).join(','),
    ...rows.map((row) =>
      [
        escapeCSVField(row.guestName),
        escapeCSVField(row.email),
        escapeCSVField(row.visitDate),
        escapeCSVField(row.reasonForVisit),
        escapeCSVField(row.registeredBy),
      ].join(',')
    ),
  ];

  return csvRows.join('\n');
}

/**
 * Generate CSV content from events data
 */
export function generateEventsCSV(events: Event[]): string {
  // Convert events to export rows
  const rows = events.map((event) => {
    return {
      title: event.title,
      description: event.description || '',
      startTime: formatDateTime(event.start_time),
      endTime: formatDateTime(event.end_time),
      location: event.location || '',
      eventType: formatEventType(event.event_type),
      capacity: event.capacity?.toString() || '',
      isCancelled: event.is_cancelled ? 'Yes' : 'No',
      createdAt: formatDateTime(event.created_at),
    };
  });

  // Generate CSV content
  const headers = [
    'Title',
    'Description',
    'Start Time',
    'End Time',
    'Location',
    'Event Type',
    'Capacity',
    'Is Cancelled',
    'Created At',
  ];
  const csvRows = [
    headers.map(escapeCSVField).join(','),
    ...rows.map((row) =>
      [
        escapeCSVField(row.title),
        escapeCSVField(row.description),
        escapeCSVField(row.startTime),
        escapeCSVField(row.endTime),
        escapeCSVField(row.location),
        escapeCSVField(row.eventType),
        escapeCSVField(row.capacity),
        escapeCSVField(row.isCancelled),
        escapeCSVField(row.createdAt),
      ].join(',')
    ),
  ];

  return csvRows.join('\n');
}

/**
 * Trigger browser download of CSV file
 */
export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
